import { Suspense, useMemo } from "react";
import PropTypes from "prop-types";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BarChartIcon from "@mui/icons-material/BarChart";

/**
 * Collapsible statistics accordion with lazy content and optional total caption.
 * @param {Object} props
 * @param {string} props.title
 * @param {Array|null} props.items - raw rows from onDataLoad; null while loading
 * @param {(items: Array) => object} props.computeStats
 * @param {React.LazyExoticComponent<React.ComponentType<{stats: object}>>} props.StatsContent
 */
function StatsAccordionPanel({ title, items, computeStats, StatsContent }) {
  const stats = useMemo(() => {
    if (!Array.isArray(items)) return null;
    return computeStats(items);
  }, [items, computeStats]);

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 2 }} slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BarChartIcon color="action" fontSize="small" />
          <Typography variant="subtitle2">{title}</Typography>
          {typeof stats?.total === "number" && (
            <Typography variant="caption" color="text.secondary">
              ({stats.total} celkem)
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {stats ? (
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            }>
            <StatsContent stats={stats} />
          </Suspense>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Načítání statistik…
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

StatsAccordionPanel.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array,
  computeStats: PropTypes.func.isRequired,
  StatsContent: PropTypes.elementType.isRequired,
};

export default StatsAccordionPanel;
